import { FC } from 'react'
import { Table, Button, Flex, Skeleton, Divider } from 'antd'
import { useParams, useNavigate } from 'react-router'
import { StringParam, useQueryParams, withDefault } from 'use-query-params'
import { LeftOutlined } from '@ant-design/icons'
import { PageSpinner, Space, Title } from '@signifyd/components'
import { spacingXXL } from '@signifyd/ant'
import { useDashboardParams } from 'core/queryParams/useDashboardParams'
import { useGetSpec } from 'lib/queries/useGetSpec/useGetSpec'
import { allRunsColumns } from './allRunsColumns.config'
import { selectedRunColumns } from './selectedRunColumnsConfig'
import { useGetSpecsForRun } from './useGetSpecsForRun'
import styles from './SpecFileDetails.module.less'

const SpecFileDetails: FC = () => {
  const { specFile, projectName, runId } = useParams()
  const navigate = useNavigate()

  const {
    query: { suite },
  } = useDashboardParams()

  const [{ selectedRunId }, setQuery] = useQueryParams({
    selectedRunId: withDefault(StringParam, runId),
  })

  const { specs, isLoading: areSpecsLoading } = useGetSpecsForRun(
    specFile,
    projectName
  )

  const { data: selectedRun, isLoading: isSelectedRunLoading } = useGetSpec({
    suite,
    project: projectName,
    runId: selectedRunId,
    specFile,
  })

  if (areSpecsLoading) {
    return <PageSpinner />
  }

  const navPrefix = suite ? `?suite=${suite}` : ''

  return (
    <div className={styles.specFileDetailsContainer}>
      <Flex justify="space-between">
        <Button
          className={styles.customLinkButton}
          onClick={() =>
            navigate(`/playwright/${projectName}/${runId}${navPrefix}`)
          }
          icon={<LeftOutlined />}
        >
          Back to current run summary
        </Button>

        {selectedRunId !== runId && (
          <Button
            onClick={() =>
              navigate(
                `/playwright/${projectName}/${selectedRunId}${navPrefix}`
              )
            }
          >
            Go to selected run summary
          </Button>
        )}
      </Flex>

      <>
        <Space size="lg" />
        <Divider />
        {isSelectedRunLoading && <Skeleton />}
        {selectedRunId && selectedRun && projectName && (
          <>
            <Title level={4}>
              {specFile} test cases - run {selectedRunId}
            </Title>
            <Table
              rowKey="title"
              loading={isSelectedRunLoading}
              columns={selectedRunColumns({
                project: projectName,
                runId: selectedRunId,
                suite,
              })}
              dataSource={selectedRun.tests}
              pagination={false}
              bordered
            />
          </>
        )}
        <Space size={spacingXXL} />
        <Divider />
        <Title level={3}>All runs for {specFile}</Title>
        <Table
          rowKey={(record) => record.runId}
          columns={allRunsColumns}
          dataSource={specs}
          pagination={false}
          bordered
          rowSelection={{
            type: 'radio',
            onChange: (_, selectedRows) => {
              setQuery({ selectedRunId: selectedRows[0].runId })
            },
            selectedRowKeys: [selectedRunId as React.Key],
          }}
        />
      </>
    </div>
  )
}

export default SpecFileDetails
